// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility function for exporting parts inventory data as CSV.
 * Follows the same pattern as other CSV exports in the frontend.
 */
import { BulkItemInventoryItem, getTotalCount } from '@/inventory/api/types';
import { toZoneName } from '@/inventory/utilities/to-zone-name';
import {
  buildCSVContent,
  escapeCSV,
  generateCSVFilename,
  triggerCSVDownload,
} from '@/utilities/csv-export';

/**
 * Transforms parts inventory data into CSV format and triggers download.
 * For sites with zones, outputs one row per zone entry.
 * For sites without zones, outputs a single row with an empty zone column.
 *
 * CSV format:
 * - Headers: "SKU", "Part name", "Location", "Zone", "Quantity"
 * - One row per part-location-zone combination
 *
 * @param data - The parts inventory data to export (array of BulkItemInventoryItem)
 */
export function exportBulkItemInventoryCsv(
  data: BulkItemInventoryItem[]
): void {
  const headers = ['SKU', 'Part name', 'Location', 'Zone', 'Quantity'];

  const rows: string[][] = [];

  for (const item of data) {
    const hasZones = item.location.zoneCount > 0;

    if (!hasZones) {
      // Site without zones — single row, empty zone column
      rows.push([
        escapeCSV(item.item.sku),
        escapeCSV(item.item.name),
        escapeCSV(item.location.name),
        '',
        getTotalCount(item.inventory).toString(),
      ]);
      continue;
    }

    // Site with zones — one row per location holding stock, the site's own floor as its default zone
    for (const entry of item.inventory) {
      if (entry.count === 0) continue;
      const zoneName = toZoneName(entry, item.location.id);
      rows.push([
        escapeCSV(item.item.sku),
        escapeCSV(item.item.name),
        escapeCSV(item.location.name),
        escapeCSV(zoneName),
        entry.count.toString(),
      ]);
    }
  }

  const csvContent = buildCSVContent(headers, rows);
  triggerCSVDownload(csvContent, generateCSVFilename('parts-inventory'));
}
